import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router';
import { MainHeader } from '../components/MainHeader';
import { Footer } from '../components/Footer';
import { PrimaryButton } from '../components/PrimaryButton';
import imgLogoCherry from '../../assets/logo-cherry-color.webp';
import imgAvatarPlaceholder from '../../assets/messenger-lisa.png';
import {
  getPublicProfessional,
  listPublicReviewsForPro,
  type PublicProfessional,
  type PublicReview,
} from '../../lib/booking-api';
import {
  patchBookingDraft,
  useBookingDraft,
} from '../../lib/booking-draft';
import { toast } from '../../lib/toast';

const REVIEWS_PER_PAGE = 3;

const formatMoney = (amount: number, currency: string) => {
  try {
    return new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency,
      maximumFractionDigits: amount % 1 === 0 ? 0 : 2,
    }).format(amount);
  } catch {
    return `${currency} ${amount}`;
  }
};

const formatDuration = (m: number): string => {
  if (!m || m < 1) return '';
  if (m < 60) return `${m} mins`;
  const h = Math.floor(m / 60);
  const rem = m % 60;
  return rem === 0 ? `${h}h` : `${h}h ${rem}m`;
};

const formatReviewDate = (iso: string): string => {
  try {
    return new Date(iso).toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'short',
      day: '2-digit',
    });
  } catch {
    return '';
  }
};

const composeName = (
  first: string | null | undefined,
  last: string | null | undefined,
  fallback = 'Professional',
): string => {
  const parts = [first, last].filter((x): x is string => !!x && x.trim() !== '');
  return parts.join(' ').trim() || fallback;
};

export default function PublicProfilePage() {
  const navigate = useNavigate();
  const draft = useBookingDraft();
  const [searchParams] = useSearchParams();
  // Pro id can be passed by either the search grid (`?id=...`) or
  // pulled from the booking draft if the customer's already locked
  // a pro in. Falling back to the draft means refresh / direct nav
  // continues to work after they've picked someone.
  const proId = searchParams.get('id') ?? draft?.professional_id ?? null;

  const [pro, setPro] = useState<PublicProfessional | null>(null);
  const [reviews, setReviews] = useState<PublicReview[]>([]);
  const [reviewsTotal, setReviewsTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [reviewsLoading, setReviewsLoading] = useState(false);

  const [serviceQuantities, setServiceQuantities] = useState<
    Record<string, number>
  >({});
  // Pro-authored add-ons the customer has added (id → quantity).
  const [addonQuantities, setAddonQuantities] = useState<
    Record<string, number>
  >({});

  // Portfolio lightbox — index of the image being viewed full-screen, or
  // null when closed. Lets customers click-to-enlarge a pro's work.
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);
  const portfolioCount = pro?.portfolio_images.length ?? 0;
  // Keyboard nav while the lightbox is open: Esc closes, ←/→ step through.
  useEffect(() => {
    if (lightboxIndex === null || portfolioCount === 0) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setLightboxIndex(null);
      else if (e.key === 'ArrowRight')
        setLightboxIndex((i) => (i === null ? i : (i + 1) % portfolioCount));
      else if (e.key === 'ArrowLeft')
        setLightboxIndex((i) =>
          i === null ? i : (i - 1 + portfolioCount) % portfolioCount,
        );
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [lightboxIndex, portfolioCount]);

  const loadPro = async () => {
    if (!proId) {
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const res = await getPublicProfessional(proId);
      setPro(res.info);
    } catch (err: any) {
      const msg =
        err?.response?.data?.message ??
        err?.message ??
        'Could not load this professional.';
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  const loadReviews = async () => {
    if (!proId) return;
    setReviewsLoading(true);
    try {
      const res = await listPublicReviewsForPro(proId, {
        limit: REVIEWS_PER_PAGE,
        offset: 0,
      });
      setReviews(res.data ?? []);
      setReviewsTotal(res.total_count ?? 0);
    } catch {
      // Reviews are non-critical — leave any previously-loaded list
      // in place rather than blanking the page on a transient error.
    } finally {
      setReviewsLoading(false);
    }
  };

  useEffect(() => {
    loadPro();
    loadReviews();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [proId]);

  // Pre-select the service the customer picked at the start of the
  // booking flow (BookingFlowPage step 1 / SelectTreatment / the
  // landing dropdown). The draft's `service_id` is the canonical
  // source of truth; falling back to the first service keeps the
  // basket usable when the customer arrived without a draft.
  useEffect(() => {
    if (!pro || !pro.services?.length) return;
    setServiceQuantities((prev) => {
      // Don't clobber a quantity the customer has already adjusted.
      if (Object.values(prev).some((n) => n > 0)) return prev;
      const draftServiceId = draft?.service_id ?? null;
      const matched = draftServiceId
        ? pro.services.find((s) => s.id === draftServiceId)
        : null;
      const target = matched ?? pro.services.find((s) => s.id);
      if (!target?.id) return prev;
      return { [target.id]: 1 };
    });
  }, [pro, draft?.service_id]);

  const updateQuantity = (id: string, delta: number) => {
    setServiceQuantities((prev) => {
      const current = prev[id] || 0;
      const next = Math.max(0, current + delta);
      return { ...prev, [id]: next };
    });
  };

  const updateAddonQuantity = (id: string, delta: number) => {
    setAddonQuantities((prev) => {
      const current = prev[id] || 0;
      const next = Math.max(0, current + delta);
      return { ...prev, [id]: next };
    });
  };

  const handleContinueToCheckout = () => {
    if (!proId || !pro) return;
    // Build the booking items from the services the customer actually
    // picked here — this was previously dropped, so the schedule +
    // checkout steps saw no services. The first picked service drives
    // the primary service_id / category, and the slot length is the
    // summed duration of everything selected.
    const selected = pro.services.filter(
      (s) => s.id && (serviceQuantities[s.id] || 0) > 0,
    );
    // The profile's "Available Services" list can be empty (the pro lists no
    // bookable services / add-ons here). That must NOT block checkout — the
    // customer already chose their main service earlier in the flow, so fall
    // back to the draft's service_id and continue with just that. Add-ons are
    // optional extras, never a requirement to book.
    if (selected.length === 0) {
      const fallbackServiceId = draft?.service_id ?? null;
      if (!fallbackServiceId) return;
      const fallbackAddons = (pro.addons ?? []).filter(
        (a) => (addonQuantities[a.id] || 0) > 0,
      );
      patchBookingDraft({
        professional_id: proId,
        mode: draft?.mode ?? 'ONE_OFF',
        items: [
          { service_id: fallbackServiceId, quantity: 1 },
          ...fallbackAddons.map((a) => ({
            professional_addon_id: a.id,
            quantity: addonQuantities[a.id] || 1,
          })),
        ],
        service_id: fallbackServiceId,
        // Keep the category the customer chose upstream — the draft already
        // holds the right one (the normal path re-pins from the picked
        // service, which we don't have here).
        service_category_id: draft?.service_category_id ?? null,
        duration_minutes: draft?.duration_minutes || 60,
        currency: draft?.currency,
        meta: { ...(draft?.meta ?? {}), flow_entry: 'TREATMENT' },
      });
      navigate('/your-appointment');
      return;
    }

    const selectedAddons = (pro.addons ?? []).filter(
      (a) => (addonQuantities[a.id] || 0) > 0,
    );

    const items = [
      ...selected.map((s) => ({
        service_id: s.id as string,
        quantity: serviceQuantities[s.id as string] || 1,
      })),
      ...selectedAddons.map((a) => ({
        professional_addon_id: a.id,
        quantity: addonQuantities[a.id] || 1,
      })),
    ];
    const totalDuration =
      selected.reduce(
        (sum, s) =>
          sum +
          (s.duration_minutes || 0) * (serviceQuantities[s.id as string] || 0),
        0,
      ) +
      selectedAddons.reduce(
        (sum, a) =>
          sum + (a.duration_minutes || 0) * (addonQuantities[a.id] || 0),
        0,
      );
    const primary = selected[0];

    patchBookingDraft({
      professional_id: proId,
      mode: draft?.mode ?? 'ONE_OFF',
      items,
      service_id: primary.id ?? draft?.service_id ?? null,
      // Pin to THIS treatment's category. Never fall back to the draft's
      // existing service_category_id — a stale value (e.g. a previous
      // cleaning booking) would later mis-route the checkout Back button
      // into the cleaning wizard. Null is fine; the checkout resolves the
      // category from the chosen service if needed.
      service_category_id: primary.category?.id ?? null,
      duration_minutes: totalDuration || draft?.duration_minutes || 60,
      currency: primary.currency ?? draft?.currency,
      // Stamp the entry flow so the checkout page's Back button routes
      // through the TREATMENT flow (pro profile) regardless of catalog
      // load state or a stale service_category_id. Spread existing meta —
      // patchBookingDraft shallow-merges, so a bare `meta` would wipe it.
      meta: { ...(draft?.meta ?? {}), flow_entry: 'TREATMENT' },
    });
    navigate('/your-appointment');
  };

  const displayName = pro ? composeName(pro.user?.first_name, pro.user?.last_name) : '';
  const ratingDisplay = pro?.rating_avg ? Number(pro.rating_avg).toFixed(2) : '—';
  // Prefer the presigned preview URLs — the canonical `*_url` columns
  // point at a private bucket and won't load in an <img>.
  const profileImage =
    pro?.user?.profile_pic_preview_url ??
    pro?.cover_image_preview_url ??
    imgAvatarPlaceholder;

  const selectedCount = useMemo(
    () => Object.values(serviceQuantities).reduce((sum, n) => sum + n, 0),
    [serviceQuantities],
  );

  // Running subtotal across every selected service — surfaces above
  // the "Continue to checkout" button so the price visibly tracks
  // the customer's +/− taps. Tax + booking-charges are applied later
  // on the basket page; this is just the line-item total.
  const subtotalSummary = useMemo(() => {
    let total = 0;
    let currency = 'GBP';
    for (const s of pro?.services ?? []) {
      const qty = serviceQuantities[s.id ?? ''] || 0;
      if (qty <= 0) continue;
      // Inclusive price (matches the all-in basket); fall back to raw.
      total += Number(s.display_price ?? s.price ?? 0) * qty;
      if (s.currency) currency = s.currency;
    }
    for (const a of pro?.addons ?? []) {
      const qty = addonQuantities[a.id] || 0;
      if (qty <= 0) continue;
      total += Number(a.display_price ?? a.price ?? 0) * qty;
      if (a.currency) currency = a.currency;
    }
    return { amount: total, currency };
  }, [pro, serviceQuantities, addonQuantities]);

  // ---------- Render branches ----------
  if (!proId) {
    return (
      <div className="min-h-screen bg-[#f2eee2]">
        <MainHeader />
        <main className="max-w-[1140px] mx-auto px-4 py-24 text-center">
          <span className="material-icons text-[48px] text-[#eee] mb-3">
            person_search
          </span>
          <h1 className="font-['Playfair_Display',sans-serif] font-bold text-[#8e2a2f] text-[22px] mb-2">
            Pick a professional to view their profile
          </h1>
          <p className="font-['DM_Sans',sans-serif] text-[#908e8e] text-[14px] mb-8 max-w-[420px] mx-auto">
            Start a booking and we'll show you available pros for your service
            and location.
          </p>
          <PrimaryButton onClick={() => navigate('/')} className="px-8 h-[48px]">
            Browse services
          </PrimaryButton>
        </main>
        <Footer />
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-[#f2eee2]">
        <MainHeader />
        <main className="max-w-[1140px] mx-auto px-4 py-24 text-center text-[#908e8e] font-['DM_Sans',sans-serif] text-[14px]">
          Loading professional profile…
        </main>
        <Footer />
      </div>
    );
  }

  if (!pro) {
    return (
      <div className="min-h-screen bg-[#f2eee2]">
        <MainHeader />
        <main className="max-w-[1140px] mx-auto px-4 py-24 text-center">
          <p className="font-['DM_Sans',sans-serif] text-[#991b1b] text-[14px] mb-4">
            This professional is not available.
          </p>
          <button
            type="button"
            onClick={loadPro}
            className="px-6 h-[42px] rounded-[8px] bg-[#8e2a2f] text-white font-['DM_Sans',sans-serif] font-bold text-[14px] hover:bg-[#7a242a] transition-colors"
          >
            Try again
          </button>
        </main>
        <Footer />
      </div>
    );
  }

  // ---------- Main content ----------
  return (
    <div className="min-h-screen bg-[#f2eee2]">
      <MainHeader />
      <main className="max-w-[1140px] mx-auto px-4 py-8">
        {/* Banner */}
        <div className="relative mb-14">
          <div className="h-[190px] w-full rounded-[12px] bg-white flex items-center justify-center border border-[#ede8e9] relative overflow-visible">
            <button
              onClick={() => navigate(-1)}
              className="absolute left-6 top-6 w-[44px] h-[44px] bg-[#fcfcfc] rounded-[8px] flex items-center justify-center text-[#3a3335] hover:bg-white transition-colors shadow-sm z-20"
            >
              <span className="material-icons text-[24px]">navigate_before</span>
            </button>
            <div className="w-[180px]">
              <img
                src={imgLogoCherry}
                alt="Cherry Picked"
                className="w-full h-auto object-contain"
              />
            </div>
          </div>

          <div className="absolute -bottom-28 left-1/2 -translate-x-1/2 md:left-8 md:translate-x-0 w-[144px] h-[144px] rounded-[18px] overflow-hidden border-[3px] border-[#f2eee2] bg-white shadow-md z-10">
            <img
              src={profileImage}
              alt={displayName}
              className="w-full h-full object-cover"
            />
          </div>
        </div>

        {/* Profile Info */}
        <div className="mt-32 md:mt-0 md:ml-8 mb-12 flex flex-col items-center md:items-start text-center md:text-left px-4 md:px-0">
          <div className="md:pl-[160px] md:-mt-[48px]">
            <h1 className="font-['Playfair_Display',sans-serif] font-extrabold text-[#8e2a2f] text-[22px] leading-[32px] tracking-[-0.02em] mb-1">
              {displayName || pro.business_name}
            </h1>
            <p className="font-['DM_Sans',sans-serif] text-[#908e8e] text-[16px] mb-4">
              {pro.tagline ?? ''}
              {pro.city ? (pro.tagline ? ` · ${pro.city}` : pro.city) : ''}
            </p>

            <div className="flex flex-wrap justify-center md:justify-start gap-4 md:gap-6">
              {pro.is_featured ? (
                <div className="flex items-center gap-1.5">
                  <span className="material-icons text-[#8e2a2f] !text-[18px]">
                    workspace_premium
                  </span>
                  <span className="font-['DM_Sans',sans-serif] text-[#3a3335] text-[14px] font-medium">
                    Featured pro
                  </span>
                </div>
              ) : null}
              <div className="flex items-center gap-1.5">
                <span className="material-icons text-[#8e2a2f] !text-[18px]">
                  check_circle_outline
                </span>
                <span className="font-['DM_Sans',sans-serif] text-[#3a3335] text-[14px] font-medium">
                  Verified
                </span>
              </div>
              {pro.dbs_checked ? (
                <div className="flex items-center gap-1.5">
                  <span className="material-icons text-[#8e2a2f] !text-[18px]">
                    verified_user
                  </span>
                  <span className="font-['DM_Sans',sans-serif] text-[#3a3335] text-[14px] font-medium">
                    DBS checked
                  </span>
                </div>
              ) : null}
              {pro.years_of_experience > 0 ? (
                <div className="flex items-center gap-1.5">
                  <span className="material-icons text-[#8e2a2f] !text-[18px]">
                    badge
                  </span>
                  <span className="font-['DM_Sans',sans-serif] text-[#3a3335] text-[14px] font-medium">
                    {pro.years_of_experience}+ yrs experience
                  </span>
                </div>
              ) : null}
              <div className="flex items-center gap-1.5">
                <span className="material-icons text-[#e8cd96] !text-[18px]">
                  star
                </span>
                <span className="font-['DM_Sans',sans-serif] text-[#3a3335] text-[14px] font-medium">
                  {ratingDisplay} ({pro.rating_count} reviews)
                </span>
              </div>
              {pro.minimum_booking_value && pro.minimum_booking_value > 0 ? (
                <div className="flex items-center gap-1.5">
                  <span className="material-icons text-[#8e2a2f] !text-[18px]">
                    payments
                  </span>
                  <span className="font-['DM_Sans',sans-serif] text-[#3a3335] text-[14px] font-medium">
                    Minimum booking £
                    {Number(pro.minimum_booking_value).toFixed(2)}
                  </span>
                </div>
              ) : null}
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
          {/* Sidebar */}
          <div className="lg:col-span-4 space-y-6 lg:sticky lg:top-[104px] self-start">
            {/* Score Card */}
            <div className="bg-white rounded-[10px] p-6 border border-[#ede8e9] shadow-sm flex flex-col items-start text-left">
              <div className="flex items-center gap-4 mb-2">
                <span className="material-icons text-[#8e2a2f] text-[36px]">
                  star
                </span>
                <span className="font-['DM_Sans',sans-serif] font-extrabold text-[#3a3335] text-[34px] leading-none tracking-[-1px]">
                  {ratingDisplay}
                </span>
              </div>
              <p className="font-['DM_Sans',sans-serif] text-[#908e8e] text-[14px]">
                Avg. score based on {pro.rating_count} review
                {pro.rating_count === 1 ? '' : 's'}
              </p>
            </div>

            {/* About Card */}
            <div className="bg-white rounded-[10px] p-6 border border-[#ede8e9] shadow-sm space-y-5">
              <h2 className="font-['Playfair_Display',sans-serif] font-bold text-[#8e2a2f] text-[16px]">
                About
              </h2>

              <div className="space-y-6">
                {pro.city || pro.travel_radius_miles > 0 ? (
                  <div className="flex items-center gap-4">
                    <div className="w-[48px] h-[48px] rounded-[4px] bg-[#3a3335]/[0.04] flex items-center justify-center">
                      <span className="material-icons text-[#908e8e]">
                        location_on
                      </span>
                    </div>
                    <p className="font-['DM_Sans',sans-serif] font-bold text-[#3a3335] text-[14px] leading-[21px] tracking-[-0.1px]">
                      {/* The column is named travel_radius_miles for legacy
                          reasons but the whole app stores + displays it
                          in miles (the pro slider, the booking radius,
                          everything else). Don't show "km" here. */}
                      {pro.travel_radius_miles > 0
                        ? `Up to ${pro.travel_radius_miles} ${pro.travel_radius_miles === 1 ? 'mile' : 'miles'} from`
                        : 'Based in'}
                      <br />
                      {pro.city ?? '—'}
                    </p>
                  </div>
                ) : null}

                {pro.languages.length > 0 ? (
                  <div className="flex items-center gap-4">
                    <div className="w-[48px] h-[48px] rounded-[4px] bg-[#3a3335]/[0.04] flex items-center justify-center">
                      <span className="material-icons text-[#908e8e]">
                        translate
                      </span>
                    </div>
                    <p className="font-['DM_Sans',sans-serif] font-bold text-[#3a3335] text-[14px] leading-[21px] tracking-[-0.1px]">
                      Speaks {pro.languages.join(', ')}
                    </p>
                  </div>
                ) : null}
              </div>
            </div>
          </div>

          {/* Main Content */}
          <div className="lg:col-span-8 space-y-8">
            {/* Bio Card */}
            {pro.bio ? (
              <div className="bg-white/90 backdrop-blur-sm rounded-[10px] p-6 border border-[#ede8e9] shadow-sm">
                <h2 className="font-['Playfair_Display',sans-serif] font-bold text-[#8e2a2f] text-[16px] mb-3">
                  More about {displayName.split(' ')[0]}
                </h2>
                <p className="font-['DM_Sans',sans-serif] text-[#3a3335] text-[16px] leading-[28px] whitespace-pre-line">
                  {pro.bio}
                </p>
              </div>
            ) : null}

            {/* Gallery Card */}
            {pro.portfolio_images.length > 0 ? (
              <div className="bg-white/90 backdrop-blur-sm rounded-[10px] p-6 border border-[#ede8e9] shadow-sm">
                <h2 className="font-['Playfair_Display',sans-serif] font-bold text-[#8e2a2f] text-[16px] mb-5">
                  Work Portfolio
                </h2>

                {/* Mobile: a 2-row strip that swipes horizontally — ~4
                    images (2×2) are visible at a time, the rest are a
                    swipe away. Keeps the profile tidy on small screens. */}
                <div className="grid grid-rows-2 grid-flow-col auto-cols-[47%] gap-3 overflow-x-auto snap-x snap-mandatory pb-2 -mx-1 px-1 sm:hidden [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
                  {pro.portfolio_images.map((p, i) => (
                    <button
                      key={i}
                      type="button"
                      onClick={() => setLightboxIndex(i)}
                      className="aspect-square rounded-[12px] overflow-hidden border border-[#eee] shadow-sm snap-start focus:outline-none focus:ring-2 focus:ring-[#8e2a2f]"
                      aria-label={`Enlarge ${p.caption ?? `work image ${i + 1}`}`}
                    >
                      <img
                        src={p.preview_url ?? p.url}
                        alt={p.caption ?? `Work ${i + 1}`}
                        className="w-full h-full object-cover"
                      />
                    </button>
                  ))}
                </div>

                {/* Tablet / desktop: the full responsive grid. */}
                <div className="hidden sm:grid sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
                  {pro.portfolio_images.map((p, i) => (
                    <button
                      key={i}
                      type="button"
                      onClick={() => setLightboxIndex(i)}
                      className="group aspect-square rounded-[12px] overflow-hidden border border-[#eee] shadow-sm hover:shadow-md transition-shadow cursor-zoom-in focus:outline-none focus:ring-2 focus:ring-[#8e2a2f]"
                      aria-label={`Enlarge ${p.caption ?? `work image ${i + 1}`}`}
                    >
                      <img
                        src={p.preview_url ?? p.url}
                        alt={p.caption ?? `Work ${i + 1}`}
                        className="w-full h-full object-cover transition-transform duration-200 group-hover:scale-105"
                      />
                    </button>
                  ))}
                </div>
              </div>
            ) : null}

            {/* Services Card */}
            <div className="bg-white/90 backdrop-blur-sm rounded-[10px] p-6 border border-[#ede8e9] shadow-sm">
              <div className="mb-5">
                <h2 className="font-['Playfair_Display',sans-serif] font-bold text-[#8e2a2f] text-[16px]">
                  Available Services
                </h2>
              </div>

              {pro.services.length === 0 ? (
                <p className="font-['DM_Sans',sans-serif] text-[#908e8e] text-[14px] py-8 text-center">
                  This professional hasn't listed any services yet.
                </p>
              ) : (
                <div className="space-y-0 divide-y divide-[#ede8e9]">
                  {pro.services
                    // Skip half-configured rows — e.g. a join row whose
                    // service was deleted or never had a name set surfaces
                    // as an empty "1h £0" line otherwise.
                    .filter((service) => (service.name ?? '').trim().length > 0)
                    .map((service) => {
                    const sid = service.id ?? '';
                    const quantity = serviceQuantities[sid] || 0;
                    const isSelected = quantity > 0;
                    return (
                      <div
                        key={sid}
                        className={`px-0 py-4 transition-all duration-300 ${
                          isSelected
                            ? 'bg-[#8e2a2f] text-white rounded-[12px] my-2 shadow-lg scale-[1.02] px-4'
                            : 'hover:bg-[#fcfcfc]'
                        }`}
                      >
                        <div className="flex items-start justify-between gap-6">
                          <div className="flex-1">
                            <h3
                              className={`font-['Playfair_Display',sans-serif] font-bold text-[15px] mb-2 ${
                                isSelected ? 'text-white' : 'text-[#8e2a2f]'
                              }`}
                            >
                              {service.name}
                            </h3>
                            {isSelected && service.description ? (
                              <p className="font-['DM_Sans',sans-serif] text-white/80 text-[14px] mt-4 max-w-[500px] leading-[22px]">
                                {service.description}
                              </p>
                            ) : null}
                          </div>
                          <div className="text-right shrink-0">
                            <p
                              className={`font-['DM_Sans',sans-serif] font-bold text-[14px] mb-1 ${
                                isSelected ? 'text-[#eee]' : 'text-[#3a3335]'
                              }`}
                            >
                              {formatDuration(service.duration_minutes)}
                            </p>
                            <p
                              className={`font-['DM_Sans',sans-serif] font-bold text-[14px] ${
                                isSelected ? 'text-[#eee]' : 'text-[#3a3335]'
                              }`}
                            >
                              {quantity > 1 ? (
                                <>
                                  {formatMoney(
                                    Number(
                                      service.display_price ?? service.price,
                                    ) * quantity,
                                    service.currency,
                                  )}
                                  <span
                                    className={`ml-2 font-normal text-[12px] ${
                                      isSelected
                                        ? 'text-white/70'
                                        : 'text-[#908e8e]'
                                    }`}
                                  >
                                    ({quantity} ×{' '}
                                    {formatMoney(
                                      Number(
                                        service.display_price ?? service.price,
                                      ),
                                      service.currency,
                                    )}
                                    {service.pricing_unit === 'PER_HOUR'
                                      ? '/hr'
                                      : ''}
                                    )
                                  </span>
                                </>
                              ) : (
                                <>
                                  {formatMoney(
                                    Number(
                                      service.display_price ?? service.price,
                                    ),
                                    service.currency,
                                  )}
                                  {service.pricing_unit === 'PER_HOUR'
                                    ? '/hr'
                                    : ''}
                                </>
                              )}
                            </p>
                          </div>
                          <div className="flex items-center gap-3 ml-4">
                            <button
                              className={`w-[22px] h-[22px] rounded border-2 flex items-center justify-center transition-colors ${
                                isSelected
                                  ? 'border-white/50 text-white hover:bg-white/10'
                                  : 'border-[#908e8e] text-[#908e8e] hover:border-[#8e2a2f] hover:text-[#8e2a2f]'
                              }`}
                              onClick={() => updateQuantity(sid, -1)}
                              disabled={!sid}
                            >
                              <span className="material-icons text-[14px]">
                                remove
                              </span>
                            </button>
                            <span
                              className={`font-['DM_Sans',sans-serif] font-bold text-[15px] min-w-[12px] text-center ${
                                isSelected ? 'text-white' : 'text-[#3a3335]'
                              }`}
                            >
                              {quantity}
                            </span>
                            <button
                              className={`w-[22px] h-[22px] rounded border-2 flex items-center justify-center transition-colors ${
                                isSelected
                                  ? 'border-white/50 text-white hover:bg-white/10'
                                  : 'border-[#908e8e] text-[#908e8e] hover:border-[#8e2a2f] hover:text-[#8e2a2f]'
                              }`}
                              onClick={() => updateQuantity(sid, 1)}
                              disabled={!sid}
                            >
                              <span className="material-icons text-[14px]">
                                add
                              </span>
                            </button>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}

              {/* Pro-authored add-ons — extras the customer can tack onto
                  their booking. Only shown for Hair / Beauty & Wellness
                  pros who've created some. */}
              {(pro.addons?.length ?? 0) > 0 &&
              pro.services.some(
                (s) =>
                  s.category &&
                  ['HAIR', 'BEAUTY_WELLNESS'].includes(s.category.code),
              ) ? (
                <div className="mt-8 pt-6 border-t border-[#ede8e9]">
                  <h3 className="font-['Playfair_Display',sans-serif] font-bold text-[#8e2a2f] text-[15px] mb-4">
                    Add-ons
                  </h3>
                  <div className="space-y-0 divide-y divide-[#ede8e9]">
                    {(pro.addons ?? []).map((a) => {
                      const aqty = addonQuantities[a.id] || 0;
                      const aSel = aqty > 0;
                      return (
                        <div
                          key={a.id}
                          className={`px-0 py-4 transition-all duration-300 ${
                            aSel
                              ? 'bg-[#8e2a2f] text-white rounded-[12px] my-2 shadow-lg px-4'
                              : 'hover:bg-[#fcfcfc]'
                          }`}
                        >
                          <div className="flex items-start justify-between gap-6">
                            <div className="flex-1">
                              <h4
                                className={`font-['DM_Sans',sans-serif] font-bold text-[14px] ${
                                  aSel ? 'text-white' : 'text-[#3a3335]'
                                }`}
                              >
                                {a.name}
                              </h4>
                            </div>
                            <div className="text-right shrink-0">
                              <p
                                className={`font-['DM_Sans',sans-serif] text-[13px] mb-1 ${
                                  aSel ? 'text-[#eee]' : 'text-[#908e8e]'
                                }`}
                              >
                                {formatDuration(a.duration_minutes)}
                              </p>
                              <p
                                className={`font-['DM_Sans',sans-serif] font-bold text-[14px] ${
                                  aSel ? 'text-[#eee]' : 'text-[#3a3335]'
                                }`}
                              >
                                {aqty > 1
                                  ? formatMoney(
                                      Number(a.display_price ?? a.price) * aqty,
                                      a.currency,
                                    )
                                  : formatMoney(
                                      Number(a.display_price ?? a.price),
                                      a.currency,
                                    )}
                              </p>
                            </div>
                            <div className="flex items-center gap-3 ml-4">
                              <button
                                className={`w-[22px] h-[22px] rounded border-2 flex items-center justify-center transition-colors ${
                                  aSel
                                    ? 'border-white/50 text-white hover:bg-white/10'
                                    : 'border-[#908e8e] text-[#908e8e] hover:border-[#8e2a2f] hover:text-[#8e2a2f]'
                                }`}
                                onClick={() => updateAddonQuantity(a.id, -1)}
                              >
                                <span className="material-icons text-[14px]">
                                  remove
                                </span>
                              </button>
                              <span
                                className={`font-['DM_Sans',sans-serif] font-bold text-[15px] min-w-[12px] text-center ${
                                  aSel ? 'text-white' : 'text-[#3a3335]'
                                }`}
                              >
                                {aqty}
                              </span>
                              <button
                                className={`w-[22px] h-[22px] rounded border-2 flex items-center justify-center transition-colors ${
                                  aSel
                                    ? 'border-white/50 text-white hover:bg-white/10'
                                    : 'border-[#908e8e] text-[#908e8e] hover:border-[#8e2a2f] hover:text-[#8e2a2f]'
                                }`}
                                onClick={() => updateAddonQuantity(a.id, 1)}
                              >
                                <span className="material-icons text-[14px]">
                                  add
                                </span>
                              </button>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              ) : null}

              {/* Running subtotal — surfaces the running price so
                  customers can watch it update as they +/- services.
                  Tax + admin charges are added later on the basket. */}
              {selectedCount > 0 ? (
                <div className="flex items-center justify-between mt-8 pt-4 border-t border-[#ede8e9]">
                  <div>
                    <p className="font-['DM_Sans',sans-serif] text-[#908e8e] text-[12px] uppercase tracking-wider font-bold">
                      Subtotal
                    </p>
                    <p className="font-['DM_Sans',sans-serif] text-[#908e8e] text-[12px]">
                      {selectedCount} service{selectedCount === 1 ? '' : 's'} selected
                    </p>
                  </div>
                  <p className="font-['Playfair_Display',sans-serif] font-bold text-[#8e2a2f] text-[22px]">
                    {formatMoney(
                      subtotalSummary.amount,
                      subtotalSummary.currency,
                    )}
                  </p>
                </div>
              ) : null}

              <div className="flex justify-end mt-6">
                <PrimaryButton
                  className="!rounded-[5px] !h-[40px] !px-6"
                  onClick={handleContinueToCheckout}
                  disabled={!pro}
                >
                  Continue to checkout
                </PrimaryButton>
              </div>

              {/* Secondary "browse other pros" option — lives here on the
                  professional's profile (the pro-selection step), NOT on the
                  checkout pages. Lets the customer pick someone else if this
                  pro isn't the right fit. (Issue 9) */}
              <div className="mt-3 text-center">
                <button
                  type="button"
                  onClick={() => navigate('/select-professional')}
                  className="font-['DM_Sans',sans-serif] text-[#8e2a2f] text-[13px] font-bold underline"
                >
                  Browse other professionals in this category →
                </button>
              </div>
            </div>

            {/* Reviews Card */}
            <div className="bg-white/90 backdrop-blur-sm rounded-[10px] p-6 border border-[#ede8e9] shadow-sm space-y-5">
              <h2 className="font-['Playfair_Display',sans-serif] font-bold text-[#8e2a2f] text-[16px]">
                Customer reviews
              </h2>

              {reviewsLoading && reviews.length === 0 ? (
                <p className="font-['DM_Sans',sans-serif] text-[#908e8e] text-[14px] py-8 text-center">
                  Loading reviews…
                </p>
              ) : reviews.length === 0 ? (
                <p className="font-['DM_Sans',sans-serif] text-[#908e8e] text-[14px] py-8 text-center">
                  No reviews yet — be the first to book.
                </p>
              ) : (
                <>
                  <div className="space-y-8">
                    {reviews.map((review) => {
                      const reviewerName = composeName(
                        review.customer?.first_name,
                        review.customer?.last_name,
                        'A Cherry Picked customer',
                      );
                      return (
                        <div
                          key={review.id}
                          className="border border-[#f0f0f0] rounded-[16px] overflow-hidden shadow-sm hover:shadow-md transition-shadow"
                        >
                          <div className="p-8 pb-6">
                            {review.service?.name ? (
                              <h3 className="font-['Playfair_Display',sans-serif] font-bold text-[#8e2a2f] text-[17px] mb-4">
                                {review.service.name}
                              </h3>
                            ) : null}
                            <p className="font-['DM_Sans',sans-serif] text-[#3a3335] text-[15px] leading-[24px]">
                              {review.comment ?? 'No comment left.'}
                            </p>
                          </div>
                          <div className="bg-[#8e2a2f] p-6 px-8 flex items-center justify-between">
                            <div className="flex items-center gap-4">
                              <div className="w-[40px] h-[40px] rounded-[8px] bg-white/10 flex items-center justify-center border border-white/20">
                                <span className="material-icons text-white">
                                  person
                                </span>
                              </div>
                              <div>
                                <p className="font-['DM_Sans',sans-serif] font-bold text-white text-[15px]">
                                  {reviewerName}
                                </p>
                                <p className="font-['DM_Sans',sans-serif] text-white/60 text-[13px]">
                                  {formatReviewDate(review.created_at)}
                                </p>
                              </div>
                            </div>
                            <div className="flex items-center gap-1.5 bg-white/10 px-3 py-1.5 rounded-full backdrop-blur-md">
                              <span className="material-icons text-[#e8cd96] text-[18px]">
                                star
                              </span>
                              <span className="font-['DM_Sans',sans-serif] font-bold text-white text-[14px]">
                                {Number(review.rating).toFixed(1)}
                              </span>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>

                  {reviewsTotal > REVIEWS_PER_PAGE ? (
                    <div className="flex justify-center pt-2">
                      <button
                        type="button"
                        onClick={() =>
                          navigate(
                            `/public-reviews?id=${encodeURIComponent(proId ?? '')}`,
                          )
                        }
                        className="inline-flex items-center gap-2 rounded-[10px] border border-[#8e2a2f] bg-white px-6 h-[44px] font-['DM_Sans',sans-serif] font-bold text-[#8e2a2f] text-[14px] hover:bg-[#8e2a2f] hover:text-white transition-colors"
                      >
                        View all {reviewsTotal} reviews
                        <span className="material-icons text-[18px]">
                          arrow_forward
                        </span>
                      </button>
                    </div>
                  ) : null}
                </>
              )}
            </div>
          </div>
        </div>
      </main>

      {/* Portfolio lightbox — full-screen enlarge with prev/next. Backdrop
          click or Esc closes; arrows / on-screen chevrons step through. */}
      {lightboxIndex !== null && pro?.portfolio_images[lightboxIndex] ? (
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center bg-black/85 p-4 animate-in fade-in duration-150"
          onClick={() => setLightboxIndex(null)}
          role="dialog"
          aria-modal="true"
          aria-label="Portfolio image viewer"
        >
          <button
            type="button"
            onClick={() => setLightboxIndex(null)}
            aria-label="Close"
            className="absolute top-4 right-4 flex h-11 w-11 items-center justify-center rounded-full bg-white/10 text-white hover:bg-white/20 transition-colors"
          >
            <span className="material-icons text-[24px]">close</span>
          </button>

          {portfolioCount > 1 ? (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                setLightboxIndex(
                  (i) =>
                    i === null ? i : (i - 1 + portfolioCount) % portfolioCount,
                );
              }}
              aria-label="Previous image"
              className="absolute left-3 sm:left-6 flex h-11 w-11 items-center justify-center rounded-full bg-white/10 text-white hover:bg-white/20 transition-colors"
            >
              <span className="material-icons text-[28px]">chevron_left</span>
            </button>
          ) : null}

          <figure
            className="flex max-h-[90vh] max-w-[92vw] flex-col items-center gap-3"
            onClick={(e) => e.stopPropagation()}
          >
            <img
              src={
                pro.portfolio_images[lightboxIndex].preview_url ??
                pro.portfolio_images[lightboxIndex].url
              }
              alt={
                pro.portfolio_images[lightboxIndex].caption ??
                `Work ${lightboxIndex + 1}`
              }
              className="max-h-[82vh] max-w-[92vw] rounded-[10px] object-contain shadow-2xl"
            />
            <figcaption className="text-center text-[13px] text-white/80 font-['DM_Sans',sans-serif]">
              {pro.portfolio_images[lightboxIndex].caption
                ? `${pro.portfolio_images[lightboxIndex].caption} · `
                : ''}
              {lightboxIndex + 1} / {portfolioCount}
            </figcaption>
          </figure>

          {portfolioCount > 1 ? (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                setLightboxIndex((i) =>
                  i === null ? i : (i + 1) % portfolioCount,
                );
              }}
              aria-label="Next image"
              className="absolute right-3 sm:right-6 flex h-11 w-11 items-center justify-center rounded-full bg-white/10 text-white hover:bg-white/20 transition-colors"
            >
              <span className="material-icons text-[28px]">chevron_right</span>
            </button>
          ) : null}
        </div>
      ) : null}

      <Footer />
    </div>
  );
}
